import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, FileText, Calculator, Package, FolderOpen, UserRound,
  LogOut, Loader2, Download, ArrowRight,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { authApi, clearAuth, getToken, formatApiErrorDetail } from "../lib/authApi";
import { RfqSection } from "../components/RfqSection";
import { OrdersSection } from "../components/orders/OrdersSection";
import { ACTIVE_ORDER } from "../components/orders/OrderShared";
import { CustomerMetrics } from "../components/analytics/CustomerMetrics";

const fmtDate = (iso) => new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
const fmtINR = (n) => `₹${Number(n).toLocaleString("en-IN")}`;
const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

const Placeholder = ({ icon: Icon, title, note, testId }) => (
  <div className="p-10 rounded-xl bg-card border border-border text-center" data-testid={testId}>
    <Icon className="h-8 w-8 text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
    <h3 className="font-semibold font-heading">{title}</h3>
    <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">{note}</p>
  </div>
);

export default function CustomerPortal() {
  const [user, setUserState] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [rfqs, setRfqs] = useState([]);
  const [orders, setOrders] = useState([]);
  const [files, setFiles] = useState([]);
  const [profile, setProfile] = useState({ name: "", phone: "", company_name: "" });
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!getToken()) {
      navigate("/portal/login");
      return;
    }
    (async () => {
      try {
        const { data: me } = await authApi.get("/auth/me");
        if (me.role !== "customer") {
          toast.error(me.role === "admin" ? "Use the admin dashboard for this account." : "Partner portal is coming soon.");
          navigate(me.role === "admin" ? "/admin" : "/");
          return;
        }
        setUserState(me);
        setProfile({ name: me.name || "", phone: me.phone || "", company_name: me.company_name || "" });
        const [q, f, r, o] = await Promise.all([authApi.get("/customer/quotes"), authApi.get("/customer/files"), authApi.get("/rfqs"), authApi.get("/orders")]);
        setQuotes(q.data);
        setFiles(f.data);
        setRfqs(r.data);
        setOrders(o.data);
      } catch {
        clearAuth();
        navigate("/portal/login");
      }
    })();
  }, [navigate]);

  const saveProfile = async (e) => {
    e.preventDefault();
    if (!profile.name) {
      toast.error("Name is required.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await authApi.patch("/customer/profile", profile);
      setUserState(data);
      toast.success("Profile updated.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const downloadFile = async (f) => {
    setDownloading(f.id);
    try {
      const res = await authApi.get(`/customer/files/${f.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = f.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed.");
    } finally {
      setDownloading(null);
    }
  };

  const downloadQuotePdf = async (q) => {
    try {
      const res = await authApi.get(`/quotes/${q.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Vectron-Quote-VG-${q.id.slice(0, 8).toUpperCase()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("PDF download failed.");
    }
  };

  const logout = () => {
    clearAuth();
    navigate("/portal/login");
  };

  if (!user) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="portal-loading">
        <Loader2 className="h-6 w-6 animate-spin text-primary" strokeWidth={1.5} />
      </div>
    );
  }

  return (
    <div className="py-16 sm:py-20" data-testid="customer-portal">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-12">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary font-mono-tech mb-2">Customer Portal</p>
            <h1 className="text-3xl sm:text-4xl font-medium tracking-tight font-heading">Welcome, {user.name}</h1>
          </div>
          <Button variant="outline" onClick={logout} className="rounded-full" data-testid="portal-logout-btn">
            <LogOut className="h-4 w-4 mr-2" strokeWidth={1.5} /> Logout
          </Button>
        </div>

        <Tabs defaultValue={new URLSearchParams(window.location.search).get("tab") || "dashboard"}>
          <TabsList className="w-full sm:w-auto justify-start overflow-x-auto scrollbar-none tab-strip-fade h-auto" data-testid="portal-tabs">
            <TabsTrigger value="dashboard" data-testid="portal-tab-dashboard"><LayoutDashboard className="h-4 w-4 mr-1.5" strokeWidth={1.5} />Dashboard</TabsTrigger>
            <TabsTrigger value="rfqs" data-testid="portal-tab-rfqs"><FileText className="h-4 w-4 mr-1.5" strokeWidth={1.5} />RFQs ({rfqs.length})</TabsTrigger>
            <TabsTrigger value="quotes" data-testid="portal-tab-quotes"><Calculator className="h-4 w-4 mr-1.5" strokeWidth={1.5} />Quotes ({quotes.length})</TabsTrigger>
            <TabsTrigger value="orders" data-testid="portal-tab-orders"><Package className="h-4 w-4 mr-1.5" strokeWidth={1.5} />Orders ({orders.length})</TabsTrigger>
            <TabsTrigger value="documents" data-testid="portal-tab-documents"><FolderOpen className="h-4 w-4 mr-1.5" strokeWidth={1.5} />Documents ({files.length})</TabsTrigger>
            <TabsTrigger value="profile" data-testid="portal-tab-profile"><UserRound className="h-4 w-4 mr-1.5" strokeWidth={1.5} />Profile</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="mt-6">
            <CustomerMetrics />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {[
                { label: "My Quotes", value: quotes.length },
                { label: "My Files", value: files.length },
                { label: "Active Orders", value: orders.filter((o) => ACTIVE_ORDER(o.status)).length },
                { label: "Open RFQs", value: rfqs.filter((r) => !["APPROVED", "REJECTED", "CLOSED"].includes(r.status)).length },
              ].map((s) => (
                <div key={s.label} className="p-6 rounded-xl bg-card border border-border" data-testid={`portal-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <p className="font-mono-tech text-3xl font-bold text-primary">{s.value}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{s.label}</p>
                </div>
              ))}
            </div>
            <div className="p-8 rounded-xl bg-card border border-border">
              <h3 className="font-semibold font-heading text-lg">Start a new quote</h3>
              <p className="mt-2 text-sm text-muted-foreground max-w-lg">Get an instant indicative estimate and attach your CAD files — logged-in quotes appear here automatically.</p>
              <Button asChild className="mt-5 rounded-full" data-testid="portal-new-quote-btn">
                <Link to="/quote">Instant Quote <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} /></Link>
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="rfqs" className="mt-6">
            <RfqSection rfqs={rfqs} setRfqs={setRfqs} onOrderCreated={() => authApi.get("/orders").then((o) => setOrders(o.data)).catch(() => {})} />
          </TabsContent>

          <TabsContent value="quotes" className="mt-6 space-y-4">
            {quotes.length === 0 && <Placeholder icon={Calculator} title="No quotes yet" note="Submit an Instant Quote and it will show up here." testId="portal-quotes-empty" />}
            {quotes.map((q) => (
              <div key={q.id} className="p-6 rounded-xl bg-card border border-border" data-testid={`portal-quote-${q.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="font-mono-tech text-sm font-bold text-primary">VG-{q.id.slice(0, 8).toUpperCase()}</span>
                    <Badge variant="secondary">{q.process}</Badge>
                    <Badge variant="outline">{q.material} × {q.quantity}</Badge>
                    <Badge variant="outline">{q.status}</Badge>
                  </div>
                  <span className="text-xs text-muted-foreground">{fmtDate(q.created_at)}</span>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-4">
                  <p className="font-mono-tech text-sm font-bold">{fmtINR(q.estimate_min)} – {fmtINR(q.estimate_max)}</p>
                  <button onClick={() => downloadQuotePdf(q)} data-testid={`portal-quote-pdf-${q.id}`}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-xs font-semibold text-primary hover:bg-primary/20 transition-colors duration-200">
                    <Download className="h-3 w-3" strokeWidth={1.5} /> Quote PDF
                  </button>
                </div>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="orders" className="mt-6">
            <OrdersSection orders={orders} setOrders={setOrders} />
          </TabsContent>

          <TabsContent value="documents" className="mt-6 space-y-3">
            {files.length === 0 && <Placeholder icon={FolderOpen} title="No documents" note="CAD files you upload while logged in are stored privately here." testId="portal-files-empty" />}
            {files.map((f) => (
              <div key={f.id} className="flex items-center gap-4 p-4 rounded-xl bg-card border border-border" data-testid={`portal-file-${f.id}`}>
                <FolderOpen className="h-5 w-5 text-primary shrink-0" strokeWidth={1.5} />
                <span className="flex-1 truncate text-sm font-medium">{f.original_filename}</span>
                <span className="text-xs text-muted-foreground font-mono-tech">{fmtSize(f.size)}</span>
                <button onClick={() => downloadFile(f)} disabled={downloading === f.id} data-testid={`portal-file-download-${f.id}`}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-xs font-semibold text-primary hover:bg-primary/20 transition-colors duration-200">
                  {downloading === f.id ? <Loader2 className="h-3 w-3 animate-spin" strokeWidth={1.5} /> : <Download className="h-3 w-3" strokeWidth={1.5} />}
                  Download
                </button>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="profile" className="mt-6">
            <form onSubmit={saveProfile} className="max-w-lg space-y-5 p-8 rounded-xl bg-card border border-border" data-testid="portal-profile-form">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input value={user.email} disabled data-testid="profile-email-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pf-name">Full Name *</Label>
                <Input id="pf-name" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} data-testid="profile-name-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pf-phone">Phone</Label>
                <Input id="pf-phone" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} data-testid="profile-phone-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pf-company">Company</Label>
                <Input id="pf-company" value={profile.company_name} onChange={(e) => setProfile({ ...profile, company_name: e.target.value })} data-testid="profile-company-input" />
              </div>
              <Button type="submit" disabled={saving} className="rounded-full px-8" data-testid="profile-save-btn">
                {saving ? "Saving..." : "Save Profile"}
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
